/** Pure landing-page fold budget. All values are measured CSS pixels from LANDING-FOLD-1. */
export const LANDING_GRID_TOP = 109;
export const LANDING_GRID_GAP = 14;
export const LANDING_BOTTOM_GUARD = 20;
// SERVED SHIP 19, 2026-09-12 19:07Z: primary cell 822px - chart host 710px = 112px.
export const LANDING_CHART_CARD_CHROME = 112;
export const LANDING_CHART_FLOOR = 640;
// Full-size target floor; rule (e) permits the 1080px owner viewport's measured 97px compact body.
export const LANDING_TIDE_TILE_FLOOR = 240;
export const LANDING_TIDE_TILE_COMPACT_FLOOR = 97;
// Preserve the served 370px tide card while accounting for its measured two extra chrome pixels.
export const LANDING_TIDE_TILE_TARGET = 282;
// SERVED SHIP 19, 2026-09-12 19:07Z: tide cell 372px - tide tile 284px = 88px.
export const LANDING_TIDE_CARD_CHROME = 88;
export const LANDING_TIDE_CARD_MIN = LANDING_TIDE_TILE_FLOOR + LANDING_TIDE_CARD_CHROME;
export const LANDING_TIDE_CARD_MAX = LANDING_TIDE_TILE_TARGET + LANDING_TIDE_CARD_CHROME;
export const LANDING_CHROME_GROWTH_PX_PER_SCALE = 50;

export interface LandingFold {
  inkScale: number;
  chartChromeHeight: number;
  tideChromeHeight: number;
  chartHostHeight: number;
  chartCardHeight: number;
  tideTileHeight: number;
  tideCardHeight: number;
  tideRowTop: number;
  foldBottom: number;
}

/** Zero-height mount sentinel. The first positive resize replaces every field atomically. */
export const LANDING_FOLD_WAIT: LandingFold = Object.freeze({
  inkScale: 1,
  chartChromeHeight: 0,
  tideChromeHeight: 0,
  chartHostHeight: 0,
  chartCardHeight: 0,
  tideTileHeight: 0,
  tideCardHeight: 0,
  tideRowTop: 0,
  foldBottom: 0,
});

const clamp = (value: number, floor: number, ceiling: number) =>
  Math.max(floor, Math.min(ceiling, value));

/**
 * Spend the first viewport on the four expiry tides, then give every remaining pixel to the
 * linked charts while preserving their declared floor.
 */
export function measureLandingChrome(measured: {
  chartCell: number; chartHost: number; tideCell: number; tideTile: number;
}): { chart: number; tide: number } {
  for (const [name, value] of Object.entries(measured)) {
    if (!Number.isFinite(value) || value < 0) throw new RangeError(`landing chrome: ${name} must be finite and non-negative`);
  }
  const chart = measured.chartCell - measured.chartHost;
  const tide = measured.tideCell - measured.tideTile;
  if (chart < 0 || tide < 0) throw new RangeError("landing chrome: content cannot exceed its rendered cell");
  return { chart, tide };
}

function deriveLandingChrome(inkScale: number) {
  const growth = Math.round((inkScale - 1) * LANDING_CHROME_GROWTH_PX_PER_SCALE);
  return { chart: LANDING_CHART_CARD_CHROME + growth, tide: LANDING_TIDE_CARD_CHROME + growth };
}

export function deriveLandingFold(viewportHeight: number, requestedInkScale = 1): LandingFold | null {
  if (!Number.isFinite(viewportHeight) || viewportHeight < 0) {
    throw new RangeError(`landing fold: viewport height must be finite and positive; received ${viewportHeight}`);
  }
  if (viewportHeight === 0) return null;
  if (!Number.isFinite(requestedInkScale) || requestedInkScale <= 0) {
    throw new RangeError(`landing fold: ink scale must be finite and positive; received ${requestedInkScale}`);
  }

  const scaleBudget = 1 + Math.max(0, viewportHeight - LANDING_GRID_TOP - LANDING_GRID_GAP
    - LANDING_BOTTOM_GUARD - LANDING_TIDE_TILE_TARGET - LANDING_CHART_CARD_CHROME
    - LANDING_TIDE_CARD_CHROME - LANDING_CHART_FLOOR) / (2 * LANDING_CHROME_GROWTH_PX_PER_SCALE);
  const inkScale = Math.min(requestedInkScale, scaleBudget);
  const chrome = deriveLandingChrome(inkScale);
  const tideChromeGrowth = chrome.tide - LANDING_TIDE_CARD_CHROME;
  const tideCardMax = LANDING_TIDE_CARD_MAX + tideChromeGrowth;

  // R2010 / PANE-CALIBRATION-1 34eb4e6e: the chart takes the fold; only the tide keeps a maximum.
  const chartHostHeight = Math.max(
    viewportHeight
      - LANDING_GRID_TOP
      - tideCardMax
      - LANDING_GRID_GAP
      - LANDING_BOTTOM_GUARD
      - chrome.chart,
    LANDING_CHART_FLOOR,
  );
  const chartCardHeight = chartHostHeight + chrome.chart;
  const tideBudget = viewportHeight
    - LANDING_GRID_TOP
    - chartCardHeight
    - LANDING_GRID_GAP
    - LANDING_BOTTOM_GUARD;
  // Rule (e): compact viewports spend the remaining fold on the tide body after preserving its chrome.
  const tideCardHeight = clamp(
    tideBudget - tideChromeGrowth,
    LANDING_TIDE_CARD_CHROME + LANDING_TIDE_TILE_COMPACT_FLOOR,
    LANDING_TIDE_CARD_MAX,
  ) + tideChromeGrowth;
  const tideTileHeight = tideCardHeight - chrome.tide;
  const tideRowTop = LANDING_GRID_TOP + chartCardHeight + LANDING_GRID_GAP;

  return {
    inkScale,
    chartChromeHeight: chrome.chart,
    tideChromeHeight: chrome.tide,
    chartHostHeight,
    chartCardHeight,
    tideTileHeight,
    tideCardHeight,
    tideRowTop,
    foldBottom: tideRowTop + tideCardHeight + LANDING_BOTTOM_GUARD,
  };
}
